import React, { useEffect, useState } from 'react';
import { Stethoscope, AlertCircle } from 'lucide-react';
import { apiClient } from '../../lib/api';
import type { TelemedNetwork } from '@bloom/shared';

interface Props {
  physicianId: string;
  currentNetworkId?: string | null;
  // When true, the clinic has no NPI of its own — the network prescribes everything.
  currentTelemedOnly?: boolean;
  // When true, the clinic self-declared at registration that it has no NPI and
  // wants the doctor network — awaiting an admin to assign one.
  requestedNetwork?: boolean;
  // Hybrid gap coverage: clinic has its own NPI, network covers uncovered states.
  currentUseNetworkForUncoveredStates?: boolean;
  // Existing NPI clinic that signed the agreement and requested hybrid coverage,
  // awaiting admin approval.
  networkGapRequested?: boolean;
  // Admins manage networks; AMs see a read-only badge.
  canEdit: boolean;
}

/**
 * Clinic-level telemed network assignment. Admins pick which network a clinic
 * account uses for prescribing; network-required products then resolve a real
 * dr by the patient's state and incur the per-visit fee.
 */
export const TelemedNetworkSection: React.FC<Props> = ({ physicianId, currentNetworkId, currentTelemedOnly, requestedNetwork, currentUseNetworkForUncoveredStates, networkGapRequested, canEdit }) => {
  const [networks, setNetworks] = useState<TelemedNetwork[]>([]);
  const [selected, setSelected] = useState<string>(currentNetworkId || '');
  const [telemedOnly, setTelemedOnly] = useState<boolean>(currentTelemedOnly === true);
  const [hybrid, setHybrid] = useState<boolean>(currentUseNetworkForUncoveredStates === true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSelected(currentNetworkId || '');
    setTelemedOnly(currentTelemedOnly === true);
    setHybrid(currentUseNetworkForUncoveredStates === true);
  }, [currentNetworkId, currentTelemedOnly, currentUseNetworkForUncoveredStates, physicianId]);

  useEffect(() => {
    if (!canEdit) return;
    apiClient.getTelemedNetworks().then(res => {
      if (res.success && res.data) setNetworks(res.data.filter(n => n.isActive || n._id === currentNetworkId));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit]);

  // Persist the network + telemed-only + hybrid flags together. Clearing the
  // network also clears both (each requires a network). telemed-only and hybrid
  // are mutually exclusive (telemed-only has no NPI; hybrid has its own NPI).
  const persist = async (networkId: string, only: boolean, useHybrid: boolean) => {
    const prevSelected = selected;
    const prevOnly = telemedOnly;
    const prevHybrid = hybrid;
    setSelected(networkId);
    setTelemedOnly(only);
    setHybrid(useHybrid);
    setBusy(true);
    setError(null);
    setSaved(false);
    const res = await apiClient.assignTelemedNetwork(physicianId, networkId || null, only, useHybrid);
    setBusy(false);
    if (res.success) {
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } else {
      setError(res.error || 'Failed to update');
      setSelected(prevSelected);
      setTelemedOnly(prevOnly);
      setHybrid(prevHybrid);
    }
  };

  const onChangeNetwork = (value: string) => persist(value, value ? telemedOnly : false, value ? hybrid : false);
  const onToggleTelemedOnly = (value: boolean) => persist(selected, value, value ? false : hybrid);
  const onToggleHybrid = (value: boolean) => persist(selected, value ? false : telemedOnly, value);

  return (
    <div className="md:col-span-2 border-t border-gray-100 pt-4 mt-2">
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-2">
        <Stethoscope className="h-3.5 w-3.5" /> Telemed Network
      </label>

      {requestedNetwork && !currentTelemedOnly && (
        <div className="mb-2 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>
            This clinic registered <strong>without an NPI</strong> and requested the doctor network.
            {canEdit
              ? ' Assign a network and enable “Telemed-only” below to let it prescribe.'
              : ' An admin needs to assign a network before it can place orders.'}
          </span>
        </div>
      )}

      {networkGapRequested && !currentUseNetworkForUncoveredStates && (
        <div className="mb-2 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>
            This clinic <strong>signed the network agreement</strong> and requested 50-state coverage for the states it
            isn't licensed in.
            {canEdit
              ? ' Enable “Network gap coverage” below to turn it on.'
              : ' An admin needs to approve it.'}
          </span>
        </div>
      )}

      {canEdit ? (
        <div className="space-y-1">
          <select
            value={selected}
            disabled={busy}
            onChange={e => onChangeNetwork(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:opacity-50"
          >
            <option value="">None (no network)</option>
            {networks.map(n => (
              <option key={n._id} value={n._id}>
                {n.name}{!n.isActive ? ' (inactive)' : ''}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-400">
            Network-required products resolve a dr by the patient's state and add the per-visit fee.
          </p>
          <label className={`flex items-start gap-2 text-sm mt-2 ${selected ? 'text-gray-700 cursor-pointer' : 'text-gray-400'}`}>
            <input
              type="checkbox"
              checked={telemedOnly}
              disabled={busy || !selected}
              onChange={e => onToggleTelemedOnly(e.target.checked)}
              className="h-4 w-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
            />
            <span>
              Telemed-only clinic (no NPI)
              <span className="block text-xs text-gray-400">
                Every order is prescribed by the network — the clinic has no NPI of its own. Requires a network.
              </span>
            </span>
          </label>
          <label className={`flex items-start gap-2 text-sm mt-2 ${selected && !telemedOnly ? 'text-gray-700 cursor-pointer' : 'text-gray-400'}`}>
            <input
              type="checkbox"
              checked={hybrid}
              disabled={busy || !selected || telemedOnly}
              onChange={e => onToggleHybrid(e.target.checked)}
              className="h-4 w-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
            />
            <span>
              Network gap coverage (hybrid)
              <span className="block text-xs text-gray-400">
                The clinic prescribes under its own NPI where licensed; the network covers the states it isn't. Requires a network.
              </span>
            </span>
          </label>
          {error && <p className="text-xs text-red-600">{error}</p>}
          {saved && <p className="text-xs text-green-600">Saved</p>}
        </div>
      ) : (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          currentNetworkId ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
        }`}>
          {currentNetworkId ? (currentTelemedOnly ? 'Telemed-only (managed by admin)' : 'Assigned (managed by admin)') : 'None'}
        </span>
      )}
    </div>
  );
};

export default TelemedNetworkSection;
